import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { products, productCategories } from '@/mocks/products';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function ProductsPage() {
  const { t, i18n } = useTranslation();
  const [activeCategory, setActiveCategory] = useState<number | null>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const filteredProducts = activeCategory
    ? products.filter((p) => p.categoryId === activeCategory)
    : products;

  const activeCatName = activeCategory
    ? productCategories.find((c) => c.id === activeCategory)?.name
    : t('products.allCategories');

  const getCategoryName = (cat: (typeof productCategories)[0]) => {
    if (i18n.language === 'pt') return cat.namePt;
    if (i18n.language === 'de') return cat.nameDe;
    return cat.nameEn;
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-28 pb-12 md:pt-36 md:pb-16 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 bg-primary-100/40 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3"></div>
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-accent-100/30 rounded-full blur-3xl translate-y-1/3 -translate-x-1/4"></div>
        </div>
        <div className="container-main relative">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6">
            <a href="/" className="hover:text-primary-500 transition-colors">{t('nav.home')}</a>
            <i className="ri-arrow-right-s-line text-foreground-400"></i>
            <span className="text-foreground-800 font-medium">{t('products.title')}</span>
          </nav>

          <div className="max-w-2xl">
            <span className="inline-block px-3 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full mb-4">
              {t('products.eyebrow')}
            </span>
            <h1 className="text-3xl md:text-4xl font-heading font-semibold text-foreground-950 leading-tight mb-4">
              {t('products.title')}
            </h1>
            <p className="text-base text-foreground-600 leading-relaxed mb-6">
              {t('products.subtitle')}
            </p>
            <div className="flex items-center gap-3 text-sm text-foreground-500">
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-shopping-bag-3-line text-primary-500"></i>
                {products.length} {t('products.count')}
              </span>
              <span className="text-foreground-300">|</span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-grid-line text-accent-500"></i>
                {productCategories.length} {t('nav.categories')}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Category Tabs */}
      <section className="pb-8 md:pb-10">
        <div className="container-main">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
            <button
              onClick={() => setActiveCategory(null)}
              className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                activeCategory === null
                  ? 'bg-primary-500 text-white shadow-sm'
                  : 'bg-background-100 text-foreground-700 hover:bg-background-200/70 border border-background-200/70'
              }`}
            >
              {t('products.allCategories')}
            </button>
            {productCategories.filter((c) => c.id !== 924).map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                  activeCategory === cat.id
                    ? 'bg-primary-500 text-white shadow-sm'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200/70 border border-background-200/70'
                }`}
              >
                {getCategoryName(cat)}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Product Grid */}
      <section className="pb-16 md:pb-24">
        <div className="container-main">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground-900">
              {activeCatName}
            </h2>
            <span className="text-sm text-foreground-500">
              {filteredProducts.length} {t('products.count')}
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
            {filteredProducts.map((product) => (
              <a
                key={product.id}
                href={product.link}
                target="_blank"
                rel="noopener nofollow"
                className="group block bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-300/50 hover:shadow-sm transition-all"
              >
                <div className="aspect-square overflow-hidden bg-background-100">
                  <img
                    src={product.image}
                    alt={product.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = 'https://readdy.ai/api/search-image?query=Minimal%20product%20placeholder%20icon%20on%20light%20neutral%20background%2C%20clean%20geometric%20shape%2C%20soft%20shadows&width=400&height=400&seq=product-fallback&orientation=squarish';
                    }}
                  />
                </div>
                <div className="p-4">
                  <span className="inline-block text-xs font-medium text-accent-600 bg-accent-100/50 px-2 py-0.5 rounded-full mb-2">
                    {product.category}
                  </span>
                  <h3 className="text-sm font-medium text-foreground-900 leading-snug mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors">
                    {product.title}
                  </h3>
                  <p className="text-sm font-semibold text-primary-600 mb-3">
                    {product.price}
                  </p>
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-primary-500 bg-primary-50 px-3 py-1.5 rounded-full group-hover:bg-primary-500 group-hover:text-white transition-colors">
                    {t('products.buyNow')}
                    <i className="ri-external-link-line"></i>
                  </span>
                </div>
              </a>
            ))}
          </div>

          {filteredProducts.length === 0 && (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto rounded-full bg-background-200/70 flex items-center justify-center mb-4">
                <i className="ri-search-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-sm text-foreground-500">{t('products.noProducts')}</p>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}